import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import "@/assets/styles/components/_payment-success.scss";
import Success from "@/assets/svg/success.svg";
import Error from "@/assets/svg/payment-error.svg";
import Info from "@/assets/svg/info.svg";
import usePackages from "@/hooks/usePackages.tsx";
import { PackageData, PaymentModalProps } from "./packageModal.typs.ts";

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  type,
  paymentUrl,
  selectedPackage,
  onIframeMessage,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [currentType, setCurrentType] = useState<
    "success" | "iframe" | "error"
  >(type);
  const [dpPayReceived, setDpPayReceived] = useState(false);

  const { packages } = usePackages();

  const typedPackages = packages as PackageData[] | undefined;
  const selectedPackageData =
    typedPackages?.find((pkg) => pkg?.id === selectedPackage) || null;

  useEffect(() => {
    setCurrentType(type);
    if (isOpen && type === "iframe") {
      setDpPayReceived(false);
    }
  }, [type, isOpen]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event?.data?.dpPay === true) {
        if (event?.data?.data?.error === true) {
          setCurrentType("error");
          setDpPayReceived(false);
        } else {
          setCurrentType("success");
          setDpPayReceived(true);
        }
      }

      if (onIframeMessage) {
        onIframeMessage(event);
      }
    };

    if (isOpen && type === "iframe") {
      window.addEventListener("message", handleMessage);
    }

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [isOpen, type, onIframeMessage]);

  const handleClose = () => {
    if (dpPayReceived) {
      if (onIframeMessage) {
        onIframeMessage({
          data: { dpPay: true, resetRequired: true },
        } as MessageEvent);
      }
    }

    onClose();
  };

  const renderContent = () => {
    switch (currentType) {
    case "success":
      return (
        <div className="payment-modal-success">
          <div className="success-icon">
            <img src={Success} alt="Success" />
          </div>

          <div className="modal-header">
            <h2 className="modal-title">Tebrikler!</h2>
            <p className="content-title">Ödeme Başarıyla Tamamlandı!</p>
          </div>

          <div className="payment-details">
            <div className="detail-row">
              <span className="detail-label">Sipariş Özeti</span>
            </div>

            <div className="detail-row">
              <div className="price">
                <span className="detail-value">
                  {selectedPackageData?.description}
                </span>
                <span>{selectedPackageData?.price}₺</span>
              </div>
            </div>

            <div className="detail-row">
              <span className="detail-value reference-number">
                <img src={Info} alt="Info Icon" />
                <span className="bold-text">Geçerlilik Süresi:</span> Satın
                  aldığınız paket 1 yıl süreyle geçerlidir.
              </span>
            </div>
          </div>
        </div>
      );

    case "iframe":
      return (
        <div className="payment-modal-iframe-container">
          <div className="payment-modal-body">
            {paymentUrl && (
              <iframe
                ref={iframeRef}
                src={paymentUrl}
                className="payment-modal-iframe"
                title="Ödeme İşlemi"
                allow="payment"
                sandbox="allow-forms allow-scripts allow-same-origin allow-top-navigation"
              />
            )}
          </div>
        </div>
      );

    case "error":
      return (
        <div className="payment-modal-error">
          <div className="success-icon">
            <img src={Error} alt="Success" />
          </div>

          <div className="modal-header">
            <h2 className="modal-title">Üzgünüz!</h2>
            <p className="content-title">Ödeme İşlemi Tamamlanamadı!</p>
          </div>

          <div className="payment-details">
            <p>
                Bu durum kartınızın limit, doğrulama ya da banka kaynaklı
                güvenlik önlemlerinden kaynaklanıyor olabilir. Lütfen kart
                bilgilerinizi kontrol ederek tekrar deneyin ya da farklı bir
                ödeme yöntemi kullanın.
            </p>
          </div>

          <button className="try-again" onClick={onClose}>
              Tekrar Dene
          </button>
        </div>
      );

    default:
      return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className={`payment-modal payment-modal-${currentType}`}
      overlayClassName="payment-modal-overlay"
      contentLabel="Payment Modal"
      shouldCloseOnOverlayClick={currentType !== "iframe"}
      shouldCloseOnEsc={false}
      ariaHideApp={false}
    >
      {currentType !== "iframe" && (
        <button className="close-button" onClick={handleClose}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="#999"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {renderContent()}
    </Modal>
  );
};
